import { Grid } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { shallow } from "zustand/shallow";

import { EXCLUSIONS_CONFIG } from "../../../constants";
import useIntakeFormStore from "../../../store/IntakeForm.store";
import Card from "../../Card";
import Checkbox from "../../Form/Checkbox";
import FormSlider from "../../Form/FormSlider";
import SelectInput from "../../Form/SelectInput";
import TransferList from "../../Form/TransferList";
import ProgressBar from "../../ProgressBar";
import CreditQualityField from "../CreditQualityGroupField";

import "./specialinstructions.scss";


const fieldsMap = {
  maturityPreference: "custMaturity",
  sectorPreference: "custSector",
  statePreference: "custStatePref",
  yieldPreference: "custYield",
  esgPreference: "custEsg",
  gicsPreference: "custIndustryGrpExclusion",
  esgScreenPreference: "custEsgExclusion",
  creditQuality: "custCreditQuality",
};
/* eslint-disable sonarjs/no-duplicate-string */
const SpecialInstructionsCard = ({ id, headerIcon, isVisible, isEnabled }) => {
  const [isCardDisabled, setIsCardDisabled] = useState(false);
  const [strategyCustomizations, setStrategyCustomizations] = useState([]);
  const [strategyRange, setStrategyRange] = useState([1, 10]);
  const [esgOptions, setEsgOptions] = useState([]);
  const [statePreferenceOptions, setStatePreferenceOptions] = useState([]);
  const [esgScreenOptions, setEsgScreenOtpions] = useState([]);
  const [formData, formState, formOptions] = useIntakeFormStore(
    (state) => [
      state.data.formData,
      state.data.formState,
      state.data.formOptions,
    ],
    shallow
  );

  const { control, setValue, unregister } = useFormContext();

  let selectedStrategy;

  if(formData && formData?.strategyDetail){
    selectedStrategy = formData.strategyDetail;
  } else if (formState && formState?.strategyDetail){
    selectedStrategy = formState.strategyDetail;
  }

  const isLocked = formState.isFormLocked;
  const customizations = selectedStrategy?.customizations
    ? selectedStrategy?.customizations.filter((item) => item.desc === `Y`)
    : selectedStrategy?.customizations;

  const excludedStates = useWatch({
    control,
    name: "stateExclusions",
  });

  const excludedSectors = useWatch({
    control,
    name: "sectorExclusions",
  });

  const excludedGics = useWatch({
    control,
    name: "gics_industry_group",
  });

  const excludedEsg = useWatch({
    control,
    name: "esg_screens",
  });

  const esgScreen = useWatch({
    control,
    name: 'esgScreenPreference'
  })

  const selectedState = useWatch({
    control,
    name: "statePreference",
  });

  const selectedSaturityPreference = useWatch({
    control,
    name: "maturityPreference",
  });

  const parseTemplateExclusion = (exclusions, field) =>
    exclusions
      ?.filter(
        (exclusion) =>
          exclusion.exclusionType ===
            field.charAt(0).toUpperCase() + field.slice(1) ||
          exclusion.exclusionType === field
      )
      ?.map((exclusion) => ({
        [field === "esg_screens" ? "name" : field]: exclusion.exclusion,
        weight: exclusion.weight,
      }));

  useEffect(() => {
    selectedState?.length && setValue("stateExclusions", []);
  }, [selectedState, setValue]);

  const getExcludedStatesProgress = useMemo(() => {
    if (!excludedStates) return 0;
    return excludedStates?.reduce((a, b) => a + parseFloat(b.weight) * 100, 0);
  }, [excludedStates]);

  const getExcludedSectorsProgress = useMemo(() => {
    if (!excludedSectors) return 0;
    return excludedSectors?.reduce((a, b) => a + parseFloat(b.weight) * 100, 0);
  }, [excludedSectors]);

  const getExcludedGicsProgress = useMemo(() => {
    if (!excludedGics) return 0;
    return excludedGics?.reduce((a, b) => a + parseFloat(b.weight) * 100, 0);
  }, [excludedGics]);

  useEffect(() => {
    if (formData && formOptions) {
      if(formData.esgScreen){
        setValue('esgScreenPreference', formData.esgScreen);
      }
      const maturityRange = formData?.maturitySpecialInstructions?.split("-");
      if (Array.isArray(maturityRange) && maturityRange.length > 1) {
        setValue("maturityPreference", maturityRange);
      }
    }
  }, [formData, setValue]);

  useEffect(() => {
    if(formOptions?.esgScreens){
      const options = [];
      for(let i = 0; i < formOptions.esgScreens.length; i++){
        const label = formOptions.esgScreens[i].label;
        const objArr = label.split('|');
        const option = {label: objArr[0], value: objArr[1]}
        options.push(option);
      }
      setEsgScreenOtpions(options);
    }
  }, [formOptions])


  // eslint-disable-next-line sonarjs/cognitive-complexity
  const inputFields = useMemo(() => {
    let inputs = [];
    if (selectedStrategy?.assetClass !== "Equity") {
      inputs = [
        {
          type: "slider",
          name: "maturityPreference",
          step:
            parseInt(strategyRange[1]) - parseInt(strategyRange[0]) >= 19
              ? 2
              : 1,
          label: `Maturity Range (${selectedStrategy?.maturityDatePart})`,
          min: parseInt(strategyRange[0]),
          max: parseInt(strategyRange[1]),
          options: {
            disabled: isLocked,
          },
          value: selectedSaturityPreference,
        },
        {
          type: "select",
          name: "statePreference",
          label: "State Preference (default is National)",
          items: formOptions.statePreference || [],
          options: {
            required: false,
            readOnly: isLocked,
          },
        },
        {
          type: "select",
          name: "esgPreference",
          label: "ESG",
          items: esgOptions,
          options: {
            required: false,
            readOnly: isLocked,
          },
        },
        {
          type: "checkbox",
          name: "yieldPreference",
          label: "Maximize yield to worst",
          options: {
            readOnly: isLocked,
          },
        },
        {
          type: "multi-select",
          name: "sectorPreference",
          postname: "sectorExclusions",
          label: "Sector Exclusions",
          label1: "Available",
          label2: "Selected Sectors",
          items: formOptions.sector || [],
          defaultSelectedValue:
            parseTemplateExclusion(formData?.templateExclusions, "sector") ??
            [],
          config: { ...EXCLUSIONS_CONFIG.sector },
        },
        {
          type: "progress",
          name: "sectorPreference",
          value: getExcludedSectorsProgress,
          config: { ...EXCLUSIONS_CONFIG.sector },
        },
      ];

      !selectedState &&
        inputs.push(
          ...[
            {
              type: "multi-select",
              name: "statePreference",
              postname: "stateExclusions",
              label: "State Exclusions",
              label1: "Available",
              label2: "Selected States",
              items: formOptions.state || [],
              defaultSelectedValue:
                parseTemplateExclusion(formData?.templateExclusions, "state") ??
                [],
              config: { ...EXCLUSIONS_CONFIG.state },
              readOnly: isLocked,
            },
            {
              type: "progress",
              name: "statePreference",
              value: getExcludedStatesProgress,
              config: { ...EXCLUSIONS_CONFIG.state },
            },
          ]
        );
    } else {
      inputs = [
        {
          type: "multi-select",
          name: "gicsPreference",
          postname: "gics_industry_group",
          label: "GICS Industry Group Exclusions",
          label1: "Available",
          label2: "Selected",
          items: formOptions.gics_industry_group || [],
          defaultSelectedValue:
            parseTemplateExclusion(
              formData?.templateExclusions,
              "gics_industry_group"
            ) ?? [],
          config: { ...EXCLUSIONS_CONFIG.gics_industry_group },
        },
        {
          type: "progress",
          name: "gicsPreference",
          value: getExcludedGicsProgress,
          config: { ...EXCLUSIONS_CONFIG.gics_industry_group },
        },
        {
          type: "dropdown",
          key: "esgScreenPreference",
          name: "esgScreenPreference",
          postname: "esg_screens",
          label: "Faith Based Customization Options (select only one)",
          options: {
            required: false,
            readOnly: isLocked,
            items: esgScreenOptions
          },
        }
      ];
    }
    return inputs;
  }, [
    formData,
    formOptions,
    isLocked,
    strategyRange,
    selectedState,
    selectedStrategy,
    getExcludedStatesProgress,
    getExcludedSectorsProgress,
    getExcludedGicsProgress,
    esgOptions,
    statePreferenceOptions,
  ]);
  // eslint-enable-next-line sonarjs/cognitive-complexity

  useEffect(() => {
    if (customizations) {
      // Disable card if all fields are disabled.
      //console.log('CUSTOMIZATIONS for SI: ' + JSON.stringify(customizations))
      setIsCardDisabled(!customizations.length);
      setStrategyCustomizations(
        customizations
          .filter((item) => `${item.desc}`.toUpperCase() === "Y")
          .map(({ name }) => name)
      );
      inputFields.forEach((f) => {
        if (!customizations.find(({ name }) => name === fieldsMap[f.name])) {
          setValue(f.name, undefined);
        }
      });
    }

    return () => {
      customizations &&
        inputFields.forEach((f) => {
          if (!customizations.find(({ name }) => name === fieldsMap[f.name])) {
            unregister(f.name);
          }
        });
    };
  }, [inputFields, selectedStrategy, setValue, unregister]);

  useEffect(() => {
    if (!selectedStrategy?.strategyCode) return;

    setStrategyRange((prevState) => [
      selectedStrategy.maturityLowerBound || prevState[0],
      selectedStrategy.maturityUpperBound || prevState[1],
    ]);
  }, [selectedStrategy]);

  useEffect(() => {
    if (formOptions.esg) {
      setEsgOptions(formOptions.esg);
    }
    if (formOptions.state) {
      setStatePreferenceOptions(formOptions.statePreference);
    }
  }, [formOptions]);

  useEffect(() => {
    if (!formState?.isFormLocked) {
      if (formState.category === "Municipal Bonds") {
        console.log(
          `Setting creditQuality value inside useEffect: ${selectedState}`
        );
        if (selectedState === "" || selectedState === undefined) {
          setValue("creditQuality", "A,AA,AAA");
        } else {
          setValue("creditQuality", null);
        }
        // eslint-disable-next-line sonarjs/no-collapsible-if
      } else if (formState.category === "Corporate Bonds") {
        if (selectedState === "" || selectedState === undefined) {
          setValue("creditQuality", "BBB,A,AA,AAA");
        }
      }
    }
  }, [formState.category, formState?.isFormLocked, selectedState, setValue]);

  const specialInstructionsTitle = "Special Instructions (Preferences)";
  const getSelectField = (inputField, idx) => {
    if (inputField.name === "esgPreference") {
      return (
        <SelectInput
          key={idx}
          name={inputField.name}
          label={inputField.label}
          options={{ ...inputField.options, items: esgOptions }}
        />
      );
    }
    if (inputField.name === "statePreference") {
      return (
        <SelectInput
          key={idx}
          name={inputField.name}
          label={inputField.label}
          options={{
            ...inputField.options,
            items: statePreferenceOptions,
          }}
        />
      );
    }
  };

  return (
    <Card
      id={id}
      className={!isVisible ? "card-hide" : ""}
      title={specialInstructionsTitle}
      headerIcon={headerIcon}
      openByDefault={isEnabled}
      disabled={!isEnabled || (!isLocked && isCardDisabled)}
      isLocked={isLocked}
    >
      <div className="card__content">
        {strategyCustomizations?.includes("custCreditQual") &&
          !selectedState && (
            <Grid>
              <CreditQualityField />
            </Grid>
          )}
        <Grid display={"grid"} rowGap={2}>
          {inputFields.map((inputField, idx) => {
            if (!strategyCustomizations?.includes(fieldsMap[inputField.name])) {
              return null;
            }

            if (inputField.type === "select") {
              return getSelectField(inputField, idx);
            } else if (inputField.type === "checkbox") {
              return (
                <Checkbox
                  key={idx}
                  name={inputField.name}
                  label={inputField.label}
                  options={{ ...inputField.options }}
                />
              );
            } else if (inputField.type === "dropdown") {
              return (
                <SelectInput
                  key={idx}
                  name={inputField.name}
                  label={inputField.label}
                  options={{
                    ...inputField.options
                  }}
                />
              ); 
            
            } else if (inputField.type === "slider") {
              return (
                <FormSlider
                  key={idx}
                  name={inputField.name}
                  label={inputField.label}
                  step={inputField.step}
                  min={inputField.min}
                  max={inputField.max}
                  defaultValues={inputField.value}
                  valueLabelDisplay={"on"}
                  options={{ ...inputField.options }}
                />
              );
            } else if (inputField.type === "multi-select") {
              return (
                <TransferList
                  key={idx}
                  name={inputField.postname}
                  label={inputField.label}
                  label1={inputField.label1}
                  label2={inputField.label2}
                  items={inputField.items}
                  defaultSelectedValue={inputField.defaultSelectedValue}
                  maxItems={inputField.config.maxItems}
                  maxValue={inputField.config.maxValue}
                  field={inputField.config.field}
                  readyOnly={isLocked}
                  single={inputField.single}
                />
              );
            } else if (inputField.type === "progress") {
              return (
                <ProgressBar
                  key={idx}
                  label={`Can exclude up to ${
                    inputField.config.maxItems
                      ? inputField.config.maxItems +
                        " " +
                        inputField.config.field +
                        "s or"
                      : ""
                  } ${inputField.config.maxValue * 100}% of the index weight`}
                  subLabel={`Up to ${
                    inputField.config.maxItems
                      ? `${
                          inputField.config.maxItems - excludedStates?.length
                        } additional states or `
                      : " "
                  }${(
                    inputField.config.maxValue * 100 -
                    inputField.value
                  ).toFixed(2)}% can be excluded`}
                  start={0}
                  end={inputField.config.maxValue * 100}
                  value={inputField.value}
                />
              );
            }
            return <></>;
          })}
        </Grid>
      </div>
    </Card>
  );
};

SpecialInstructionsCard.propTypes = {
  id: PropTypes.string,
  headerIcon: PropTypes.string,
  isVisible: PropTypes.bool,
  isEnabled: PropTypes.bool,
};

export default SpecialInstructionsCard;
